import { KeyRound, Pencil, User } from "lucide-react";

import { useAuth } from "src/providers/AuthProvider";
import CustomModal from "src/components/CustomModal";
import EditProfileModal from "src/components/modals/EditProfileModal";
import UpdatePassword from "src/components/modals/UpdatePassword";
import { Button } from "src/components/ui/button";
import { SettingsPanel } from "src/components/common";

const Field = ({ label, value }: { label: string; value?: string | null }) => (
	<div className="tw-rounded-[10px] tw-border tw-border-border tw-bg-bg tw-px-3 tw-py-2.5">
		<div className="tw-mb-1 tw-text-[11.5px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-text-soft">{label}</div>
		<div className="tw-break-words tw-text-[13.5px] tw-text-text">{value || "—"}</div>
	</div>
);

const Profile = () => {
	const { user } = useAuth();

	const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "—";
	const initials = `${user?.first_name?.[0] ?? ""}${user?.last_name?.[0] ?? ""}`.toUpperCase() || "PL";

	return (
		<div className="tw-space-y-4">
			<SettingsPanel
				title="Profile Settings"
				description="Your personal and business contact details."
				actions={
					<>
						<CustomModal
							title="Update Password"
							trigger={
								<Button size="sm" variant="outline" className="tw-gap-1.5">
									<KeyRound className="tw-h-4 tw-w-4" />
									Password
								</Button>
							}
						>
							<UpdatePassword />
						</CustomModal>
						<CustomModal
							title="Edit Profile"
							trigger={
								<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
									<Pencil className="tw-h-4 tw-w-4" />
									Edit Profile
								</Button>
							}
						>
							{user && <EditProfileModal user={user} />}
						</CustomModal>
					</>
				}
			>
				<div className="tw-mb-5 tw-flex tw-items-center tw-gap-4">
					{user?.image ? (
						<img
							src={user.image}
							alt=""
							className="tw-h-16 tw-w-16 tw-rounded-full tw-border tw-border-border tw-object-cover"
						/>
					) : (
						<div className="tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-bg-teal-tint tw-text-[20px] tw-font-bold tw-text-teal-dark">
							{initials}
						</div>
					)}
					<div className="tw-min-w-0">
						<div className="tw-truncate tw-text-[17px] tw-font-bold tw-text-text">{fullName}</div>
						<div className="tw-truncate tw-text-[13px] tw-text-text-soft">{user?.email}</div>
					</div>
				</div>

				<h3 className="tw-mb-2.5 tw-flex tw-items-center tw-gap-1.5 tw-text-[13px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-text-soft">
					<User className="tw-h-3.5 tw-w-3.5" />
					Primary Contact
				</h3>
				<div className="tw-mb-5 tw-grid tw-gap-3 sm:tw-grid-cols-2">
					<Field label="Full Name" value={fullName} />
					<Field label="Phone" value={user?.phone} />
					<Field label="Email" value={user?.email} />
					<Field label="Contact Address" value={user?.primary_contact_address} />
				</div>

				<h3 className="tw-mb-2.5 tw-text-[13px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-text-soft">Business Contact</h3>
				<div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
					<Field label="Business Name" value={user?.business_name} />
					<Field label="Business Address" value={user?.business_address} />
				</div>
			</SettingsPanel>
		</div>
	);
};

export default Profile;
